// app/frontend/pages/Admin/Ads/AdShow.tsx
import React from 'react';
import { usePage, router } from '@inertiajs/react';
import { PageProps } from '@inertiajs/core';
import {
  Container,
  Title,
  Button,
  Group,
  Stack,
  Paper,
  Text,
  Grid,
  Image,
  Badge,
  Card,
  Divider,
  Table,
  Box,
  Progress,
  Anchor,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconEdit,
  IconTrash,
  IconToggleLeft,
  IconToggleRight,
  IconExternalLink,
  IconCalendar,
  IconEye,
  IconClick,
  IconChartBar,
  IconCopy,
} from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';

interface Ad {
  id: number;
  title: string;
  description?: string;
  url: string;
  banner_url?: string;
  active: boolean;
  start_date?: string;
  end_date?: string;
  priority: number;
  ad_type: 'card' | 'banner' | 'popup';
  display_frequency: number;
  click_count: number;
  impression_count: number;
  click_through_rate: number;
  created_at: string;
  updated_at: string;
  currently_active: boolean;
}

interface AdShowProps extends PageProps {
  ad: Ad;
}

export default function AdShow() {
  const { ad } = usePage<AdShowProps>().props;

  const handleToggleActive = async () => {
    try {
      const response = await fetch(`/admin/ads/${ad.id}/toggle_active`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        credentials: 'same-origin',
      });

      const data = await response.json();

      if (data.success) {
        showNotification({
          title: 'Success',
          message: data.message,
          color: 'green',
        });

        router.reload({ only: ['ad'] });
      } else {
        throw new Error('Failed to toggle ad status');
      }
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Failed to update ad status',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete the ad "${ad.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/admin/ads/${ad.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        credentials: 'same-origin',
      });

      const data = await response.json();

      if (data.success) {
        showNotification({
          title: 'Success',
          message: data.message,
          color: 'green',
        });

        router.visit('/admin/ads');
      } else {
        throw new Error('Failed to delete ad');
      }
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Failed to delete ad',
        color: 'red',
      });
    }
  };

  const getStatusBadge = () => {
    if (!ad.active) {
      return (
        <Badge color="gray" variant="filled" size="lg">
          Inactive
        </Badge>
      );
    }
    if (ad.currently_active) {
      return (
        <Badge color="green" variant="filled" size="lg">
          Active
        </Badge>
      );
    }
    if (ad.start_date && new Date(ad.start_date) > new Date()) {
      return (
        <Badge color="blue" variant="filled" size="lg">
          Scheduled
        </Badge>
      );
    }
    if (ad.end_date && new Date(ad.end_date) < new Date()) {
      return (
        <Badge color="orange" variant="filled" size="lg">
          Expired
        </Badge>
      );
    }
    return (
      <Badge color="gray" variant="filled" size="lg">
        Inactive
      </Badge>
    );
  };

  const getAdTypeBadge = () => {
    const colors = {
      card: 'blue',
      banner: 'orange',
      popup: 'purple',
    };
    return (
      <Badge color={colors[ad.ad_type]} variant="light" size="lg">
        {ad.ad_type} Ad
      </Badge>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const getPerformanceColor = (ctr: number) => {
    if (ctr >= 3.0) return 'green';
    if (ctr >= 1.5) return 'yellow';
    return 'red';
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <Button
              variant="light"
              color="orange"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => router.visit('/admin/ads')}
            >
              Back to Ads
            </Button>
            <div>
              <Group align="center" gap="md">
                <Title order={1} className="rubik text-white">
                  {ad.title}
                </Title>
                {getStatusBadge()}
                {getAdTypeBadge()}
              </Group>
              <Text size="sm" c="dimmed" mt="xs">
                Created {formatDate(ad.created_at)} • Last updated {formatDate(ad.updated_at)}
              </Text>
            </div>
          </Group>

          <Group>
            <Tooltip label={ad.currently_active ? 'Deactivate Ad' : 'Activate Ad'}>
              <ActionIcon
                variant="light"
                color={ad.currently_active ? 'orange' : 'green'}
                size="lg"
                onClick={handleToggleActive}
              >
                {ad.currently_active ? <IconToggleRight size={20} /> : <IconToggleLeft size={20} />}
              </ActionIcon>
            </Tooltip>

            <Button
              variant="light"
              leftSection={<IconEdit size={16} />}
              onClick={() => router.visit(`/admin/ads/${ad.id}/edit`)}
            >
              Edit
            </Button>

            <Button variant="light" color="red" leftSection={<IconTrash size={16} />} onClick={handleDelete}>
              Delete
            </Button>
          </Group>
        </Group>

        <Grid>
          {/* Left Column - Ad Details */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="lg">
              {/* Ad Preview */}
              <Paper p="lg" style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={600} size="lg" c="white">
                      Ad Preview
                    </Text>
                    <Anchor href={ad.url} target="_blank" td="none">
                      <Group gap="xs">
                        <IconExternalLink size={16} />
                        <Text size="sm">Visit Target URL</Text>
                      </Group>
                    </Anchor>
                  </Group>

                  {/* Dynamic Preview based on ad type */}
                  {ad.ad_type === 'card' && (
                    <Box style={{ maxWidth: 350 }}>
                      <Card shadow="sm" padding="lg" radius="md">
                        {ad.banner_url && (
                          <Card.Section>
                            <Image src={ad.banner_url} height={200} alt={ad.title} />
                          </Card.Section>
                        )}
                        <Group justify="space-between" mt="md" mb="xs">
                          <Text fw={500} size="sm" lineClamp={1}>
                            {ad.title}
                          </Text>
                          <Badge color="orange" size="sm">
                            Sponsored
                          </Badge>
                        </Group>
                        {ad.description && (
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {ad.description}
                          </Text>
                        )}
                        <Button color="#EA580C" fullWidth mt="md" radius="md" size="sm">
                          Learn More
                        </Button>
                      </Card>
                    </Box>
                  )}

                  {ad.ad_type === 'banner' && (
                    <Card
                      padding="lg"
                      radius="md"
                      style={{
                        height: 200,
                        backgroundImage: ad.banner_url
                          ? `url(${ad.banner_url})`
                          : 'linear-gradient(135deg, #EA580C 0%, #DC2626 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative',
                        color: 'white',
                      }}
                    >
                      <Box
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'rgba(0, 0, 0, 0.4)',
                          borderRadius: '8px',
                        }}
                      />
                      <Box
                        style={{
                          position: 'relative',
                          zIndex: 1,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Badge color="orange" size="sm" mb="md">
                          Sponsored
                        </Badge>
                        <Text fw={700} size="xl" mb="sm">
                          {ad.title}
                        </Text>
                        {ad.description && (
                          <Text size="md" lineClamp={2} opacity={0.9} mb="md">
                            {ad.description}
                          </Text>
                        )}
                        <Button color="#EA580C" size="md" w="fit-content">
                          Learn More
                        </Button>
                      </Box>
                    </Card>
                  )}

                  {ad.ad_type === 'popup' && (
                    <Box style={{ maxWidth: 400 }}>
                      <Card shadow="xl" padding="xl" radius="md">
                        <Stack gap="md">
                          <Group justify="space-between">
                            <Badge color="purple" size="sm">
                              Sponsored
                            </Badge>
                            <Text size="lg" c="dimmed" style={{ cursor: 'pointer' }}>
                              ✕
                            </Text>
                          </Group>
                          {ad.banner_url && <Image src={ad.banner_url} height={150} alt={ad.title} radius="md" />}
                          <Stack gap="xs">
                            <Text fw={600} size="xl">
                              {ad.title}
                            </Text>
                            {ad.description && (
                              <Text size="sm" c="dimmed">
                                {ad.description}
                              </Text>
                            )}
                          </Stack>
                          <Button color="#EA580C" fullWidth radius="md" size="lg">
                            Learn More
                          </Button>
                        </Stack>
                      </Card>
                    </Box>
                  )}
                </Stack>
              </Paper>

              {/* Ad Details */}
              <Paper p="lg" style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
                <Stack gap="md">
                  <Text fw={600} size="lg" c="white">
                    Ad Details
                  </Text>

                  <Table highlightOnHover verticalSpacing="sm" style={{ color: 'white' }}>
                    <Table.Tbody>
                      <Table.Tr>
                        <Table.Td fw={500}>Title</Table.Td>
                        <Table.Td>{ad.title}</Table.Td>
                      </Table.Tr>
                      {ad.description && (
                        <Table.Tr>
                          <Table.Td fw={500}>Description</Table.Td>
                          <Table.Td>{ad.description}</Table.Td>
                        </Table.Tr>
                      )}
                      <Table.Tr>
                        <Table.Td fw={500}>Target URL</Table.Td>
                        <Table.Td>
                          <Anchor href={ad.url} target="_blank" td="none">
                            {ad.url}
                          </Anchor>
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Ad Type</Table.Td>
                        <Table.Td>{getAdTypeBadge()}</Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Priority</Table.Td>
                        <Table.Td>{ad.priority}</Table.Td>
                      </Table.Tr>
                      {ad.ad_type === 'card' && (
                        <Table.Tr>
                          <Table.Td fw={500}>Display Frequency</Table.Td>
                          <Table.Td>Every {ad.display_frequency} events</Table.Td>
                        </Table.Tr>
                      )}
                      <Table.Tr>
                        <Table.Td fw={500}>Status</Table.Td>
                        <Table.Td>{getStatusBadge()}</Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Start Date</Table.Td>
                        <Table.Td>{formatDate(ad.start_date)}</Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>End Date</Table.Td>
                        <Table.Td>{formatDate(ad.end_date)}</Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Paper>
            </Stack>
          </Grid.Col>

          {/* Right Column - Performance Stats */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="lg">
              {/* Performance Overview */}
              <Paper p="lg" style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={600} size="lg" c="white">
                      Performance
                    </Text>
                    <IconChartBar size={20} color="white" />
                  </Group>

                  <Stack gap="lg">
                    {/* Impressions */}
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Group gap="xs">
                          <IconEye size={16} color="white" />
                          <Text size="sm" fw={500} c="white">
                            Impressions
                          </Text>
                        </Group>
                        <Text size="xl" fw={700} c="white">
                          {ad.impression_count.toLocaleString()}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Times this ad was shown
                      </Text>
                    </Box>

                    <Divider />

                    {/* Clicks */}
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Group gap="xs">
                          <IconClick size={16} color="white" />
                          <Text size="sm" fw={500} c="white">
                            Clicks
                          </Text>
                        </Group>
                        <Text size="xl" fw={700} c="white">
                          {ad.click_count.toLocaleString()}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Times this ad was clicked
                      </Text>
                    </Box>

                    <Divider />

                    {/* Click-Through Rate */}
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={500} c="white">
                          Click-Through Rate
                        </Text>
                        <Text size="xl" fw={700} c={getPerformanceColor(ad.click_through_rate)}>
                          {formatPercentage(ad.click_through_rate)}
                        </Text>
                      </Group>
                      <Progress
                        value={Math.min(ad.click_through_rate, 10)}
                        color={getPerformanceColor(ad.click_through_rate)}
                        size="sm"
                        mb="xs"
                      />
                      <Text size="xs" c="dimmed">
                        {ad.click_through_rate >= 3.0
                          ? 'Excellent performance!'
                          : ad.click_through_rate >= 1.5
                            ? 'Good performance'
                            : ad.impression_count > 100
                              ? 'Consider optimizing'
                              : 'Building data...'}
                      </Text>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>

              {/* Quick Actions */}
              <Paper p="lg" style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
                <Stack gap="md">
                  <Text fw={600} size="lg" c="white">
                    Quick Actions
                  </Text>

                  <Stack gap="sm">
                    <Button
                      variant="light"
                      leftSection={<IconEdit size={16} />}
                      onClick={() => router.visit(`/admin/ads/${ad.id}/edit`)}
                      fullWidth
                    >
                      Edit Ad
                    </Button>

                    <Button
                      variant="light"
                      leftSection={<IconCopy size={16} />}
                      onClick={() => console.log('Duplicate ad', ad.id)}
                      fullWidth
                    >
                      Duplicate Ad
                    </Button>

                    <Button
                      variant="light"
                      color={ad.currently_active ? 'orange' : 'green'}
                      leftSection={ad.currently_active ? <IconToggleRight size={16} /> : <IconToggleLeft size={16} />}
                      onClick={handleToggleActive}
                      fullWidth
                    >
                      {ad.currently_active ? 'Deactivate' : 'Activate'} Ad
                    </Button>

                    <Divider />

                    <Button
                      variant="light"
                      color="red"
                      leftSection={<IconTrash size={16} />}
                      onClick={handleDelete}
                      fullWidth
                    >
                      Delete Ad
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}
